/**
 * Fantasy Console 0 - Audio Engine
 *
 * PICO-8-style audio playback using Web Audio API.
 * - 4 simultaneous channels
 * - 64 SFX slots with 32 notes each
 * - 8 waveforms, 8 effects
 * - Looping support
 */

import type { SFX, SFXNote } from '@fc0/types'
import { SFX_COUNT, SFX_CHANNELS, NOTES_PER_SFX } from '@fc0/types'
import { decodeSFX, createEmptySFXBank } from './sfxCodec'

// ============================================================
// TYPES
// ============================================================

interface ScheduledNote {
  oscillator: OscillatorNode | AudioBufferSourceNode
  gain: GainNode
  pitch: number
  waveform: number
  effect: number
}

interface Channel {
  active: boolean
  currentSFX: number
  currentNote: number
  looping: boolean
  stopLooping: boolean
  scheduledNote: ScheduledNote | null
  timerId: ReturnType<typeof setTimeout> | null
}

// ============================================================
// PITCH CONVERSION
// ============================================================

/**
 * Convert pitch (0-63) to frequency in Hz.
 * Pitch 0 = C-1 (≈32.70 Hz), each step is a semitone.
 */
function pitchToFrequency(pitch: number): number {
  return 32.70319566 * Math.pow(2, pitch / 12)
}

// ============================================================
// WAVEFORM GENERATION
// ============================================================

/**
 * Build a PeriodicWave for a pulse wave with the given duty cycle.
 * Fourier series for a pulse wave with duty cycle d:
 *   a_n = (2 / (n * PI)) * sin(n * PI * d)
 *   b_n = (2 / (n * PI)) * (1 - cos(n * PI * d))
 * We use 64 harmonics for good approximation.
 */
function createPulseWave(ctx: AudioContext, dutyCycle: number): PeriodicWave {
  const harmonics = 64
  const real = new Float32Array(harmonics + 1)
  const imag = new Float32Array(harmonics + 1)

  real[0] = 0
  imag[0] = 0

  for (let n = 1; n <= harmonics; n++) {
    real[n] = 0
    imag[n] = (2 / (n * Math.PI)) * Math.sin(n * Math.PI * dutyCycle)
  }

  return ctx.createPeriodicWave(real, imag, { disableNormalization: false })
}

/**
 * Build a PeriodicWave for a rounded sawtooth (softer harmonics).
 * Like a sawtooth but with harmonics that roll off faster: amplitude = 1/n^1.5
 */
function createRoundedSawtooth(ctx: AudioContext): PeriodicWave {
  const harmonics = 64
  const real = new Float32Array(harmonics + 1)
  const imag = new Float32Array(harmonics + 1)

  real[0] = 0
  imag[0] = 0

  for (let n = 1; n <= harmonics; n++) {
    const sign = n % 2 === 0 ? 1 : -1
    real[n] = 0
    imag[n] = sign / Math.pow(n, 1.5)
  }

  return ctx.createPeriodicWave(real, imag, { disableNormalization: false })
}

/**
 * Seeded PRNG (mulberry32) for deterministic random numbers.
 * Returns a function that produces values in [0, 1) with the same
 * sequence every time for a given seed.
 */
function seededRandom(seed: number): () => number {
  let s = seed | 0
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), s | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Create a noise buffer (1 second of white noise at sample rate).
 * Uses a seeded PRNG so the noise is identical every time,
 * ensuring consistent playback of noise-based sound effects.
 */
function createNoiseBuffer(ctx: AudioContext): AudioBuffer {
  const sampleRate = ctx.sampleRate
  const buffer = ctx.createBuffer(1, sampleRate, sampleRate)
  const data = buffer.getChannelData(0)
  const rand = seededRandom(0xfc00015e)
  for (let i = 0; i < sampleRate; i++) {
    data[i] = rand() * 2 - 1
  }
  return buffer
}

// ============================================================
// AUDIO ENGINE
// ============================================================

export class AudioEngine {
  private context: AudioContext | null = null
  private masterGain: GainNode | null = null
  private channels: Channel[] = []
  private sfxBank: SFX[] = []

  // Cached waveforms
  private squareWave: PeriodicWave | null = null
  private pulseWave25: PeriodicWave | null = null
  private pulseWave12: PeriodicWave | null = null
  private roundedSaw: PeriodicWave | null = null
  private noiseBuffer: AudioBuffer | null = null

  constructor() {
    this.sfxBank = createEmptySFXBank()
    for (let i = 0; i < SFX_CHANNELS; i++) {
      this.channels.push(this.createEmptyChannel())
    }
  }

  private createEmptyChannel(): Channel {
    return {
      active: false,
      currentSFX: -1,
      currentNote: 0,
      looping: false,
      stopLooping: false,
      scheduledNote: null,
      timerId: null,
    }
  }

  /**
   * Set muted state. When muted, master gain is set to 0.
   */
  setMuted(muted: boolean): void {
    if (this.masterGain) {
      this.masterGain.gain.value = muted ? 0 : 0.5
    }
  }

  /**
   * Unlock audio by creating/resuming the AudioContext.
   * MUST be called directly from a user gesture handler (keydown, mousedown, etc.)
   * because browsers block AudioContext creation/resume outside of user gestures.
   * The game loop (requestAnimationFrame) does NOT count as a user gesture,
   * so calling this from sfx() inside _update() alone would fail.
   */
  unlockAudio(): void {
    if (this.context) {
      if (this.context.state === 'suspended') {
        this.context.resume()
      }
      return
    }

    try {
      this.context = new AudioContext()
      this.masterGain = this.context.createGain()
      this.masterGain.gain.value = 0.5 // Master volume at 50% to avoid clipping
      this.masterGain.connect(this.context.destination)

      // Pre-create custom waveforms
      this.squareWave = createPulseWave(this.context, 0.5)
      this.pulseWave25 = createPulseWave(this.context, 0.25)
      this.pulseWave12 = createPulseWave(this.context, 0.125)
      this.roundedSaw = createRoundedSawtooth(this.context)
      this.noiseBuffer = createNoiseBuffer(this.context)
    } catch {
      this.context = null
      this.masterGain = null
    }
  }

  /**
   * Ensure audio context is available. Called from playSFX.
   * If unlockAudio() was already called from a user gesture, this is a no-op.
   * Otherwise it attempts to create the context (may be suspended by browser).
   */
  private ensureContext(): void {
    if (!this.context) {
      this.unlockAudio()
    } else if (this.context.state === 'suspended') {
      this.context.resume()
    }
  }

  /**
   * Load SFX data from base64-encoded cart data.
   */
  loadSFX(base64: string | null | undefined): void {
    this.sfxBank = decodeSFX(base64)
  }

  /**
   * Play an SFX.
   * @param n SFX number 0-63, -1 to stop channel, -2 to stop looping
   * @param channel 0-3, or undefined for auto-select
   * @param offset Starting note position 0-31
   */
  playSFX(n: number, channel?: number, offset?: number): void {
    // Stop commands
    if (n === -1) {
      if (channel !== undefined && channel >= 0 && channel < SFX_CHANNELS) {
        this.stopChannel(channel)
      } else {
        this.stopAllChannels()
      }
      return
    }

    if (n === -2) {
      if (channel !== undefined && channel >= 0 && channel < SFX_CHANNELS) {
        this.channels[channel].stopLooping = true
        this.channels[channel].looping = false
      } else {
        for (let i = 0; i < SFX_CHANNELS; i++) {
          this.channels[i].stopLooping = true
          this.channels[i].looping = false
        }
      }
      return
    }

    // Validate SFX number
    if (n < 0 || n >= SFX_COUNT) return

    // Initialize audio context on first real play
    this.ensureContext()
    if (!this.context || !this.masterGain) return

    // Determine channel
    let ch: number
    if (channel !== undefined && channel >= 0 && channel < SFX_CHANNELS) {
      ch = channel
    } else {
      ch = this.findFreeChannel()
    }

    // Stop any existing sound on this channel
    this.stopChannel(ch)

    const sfx = this.sfxBank[n]
    const startNote =
      offset !== undefined
        ? Math.max(0, Math.min(NOTES_PER_SFX - 1, Math.floor(offset)))
        : 0

    // Set up channel state
    const channelState = this.channels[ch]
    channelState.active = true
    channelState.currentSFX = n
    channelState.currentNote = startNote
    channelState.looping = sfx.loop_start < sfx.loop_end
    channelState.stopLooping = false

    // Start playing notes
    this.playNextNote(ch)
  }

  /**
   * Stop a specific channel.
   */
  stopChannel(channel: number): void {
    if (channel < 0 || channel >= SFX_CHANNELS) return
    const ch = this.channels[channel]

    if (ch.timerId !== null) {
      clearTimeout(ch.timerId)
      ch.timerId = null
    }

    if (ch.scheduledNote) {
      this.stopScheduledNote(ch.scheduledNote)
      ch.scheduledNote = null
    }

    ch.active = false
    ch.currentSFX = -1
    ch.currentNote = 0
    ch.looping = false
    ch.stopLooping = false
  }

  /**
   * Stop all channels.
   */
  stopAllChannels(): void {
    for (let i = 0; i < SFX_CHANNELS; i++) {
      this.stopChannel(i)
    }
  }

  /**
   * Stop a channel with a fade-out to avoid abrupt cutoff.
   */
  stopChannelFade(channel: number, fadeMs: number = 50): void {
    if (channel < 0 || channel >= SFX_CHANNELS) return
    const ch = this.channels[channel]
    if (!ch.active || !this.context) return

    // Clear the timer so no new notes are scheduled
    if (ch.timerId !== null) {
      clearTimeout(ch.timerId)
      ch.timerId = null
    }

    // Fade out the current note
    if (ch.scheduledNote) {
      const now = this.context.currentTime
      const fadeSec = fadeMs / 1000
      try {
        ch.scheduledNote.gain.gain.cancelScheduledValues(now)
        ch.scheduledNote.gain.gain.setValueAtTime(
          ch.scheduledNote.gain.gain.value,
          now,
        )
        ch.scheduledNote.gain.gain.linearRampToValueAtTime(0, now + fadeSec)
        ch.scheduledNote.oscillator.stop(now + fadeSec + 0.01)
      } catch {
        // Oscillator may already be stopped
      }
      ch.scheduledNote = null
    }

    ch.active = false
    ch.currentSFX = -1
    ch.currentNote = 0
    ch.looping = false
    ch.stopLooping = false
  }

  /**
   * Stop all channels with a fade-out.
   */
  stopAllChannelsFade(fadeMs: number = 50): void {
    for (let i = 0; i < SFX_CHANNELS; i++) {
      this.stopChannelFade(i, fadeMs)
    }
  }

  /**
   * Preview a single note briefly (used by editor on note placement).
   */
  previewNote(
    pitch: number,
    waveform: number,
    durationMs: number = 80,
    volume: number = 5,
  ): void {
    this.ensureContext()
    if (!this.context || !this.masterGain) return

    const ctx = this.context
    const now = ctx.currentTime
    const frequency = pitchToFrequency(pitch)
    const duration = durationMs / 1000
    const gain = (volume / 7) * 0.5

    const gainNode = ctx.createGain()
    gainNode.connect(this.masterGain)
    gainNode.gain.setValueAtTime(gain, now)
    gainNode.gain.linearRampToValueAtTime(0, now + duration)

    if (waveform === 6) {
      // Noise
      const source = ctx.createBufferSource()
      source.buffer = this.noiseBuffer!
      source.loop = true
      const filter = ctx.createBiquadFilter()
      filter.type = 'bandpass'
      filter.frequency.value = frequency
      filter.Q.value = 2
      source.connect(filter)
      filter.connect(gainNode)
      source.start(now)
      source.stop(now + duration + 0.01)
    } else {
      const osc = ctx.createOscillator()
      this.setWaveform(osc, waveform)
      osc.frequency.setValueAtTime(frequency, now)
      osc.connect(gainNode)
      osc.start(now)
      osc.stop(now + duration + 0.01)
    }
  }

  /**
   * Find a free channel, or return 0 as fallback.
   */
  private findFreeChannel(): number {
    for (let i = 0; i < SFX_CHANNELS; i++) {
      if (!this.channels[i].active) return i
    }
    return 0 // All busy, override channel 0
  }

  /**
   * Stop a scheduled note cleanly (avoid clicks).
   */
  private stopScheduledNote(note: ScheduledNote): void {
    try {
      const now = this.context!.currentTime
      // Quick fade out to avoid clicks (2ms)
      note.gain.gain.cancelScheduledValues(now)
      note.gain.gain.setValueAtTime(note.gain.gain.value, now)
      note.gain.gain.linearRampToValueAtTime(0, now + 0.002)
      note.oscillator.stop(now + 0.003)
    } catch {
      // Oscillator may already be stopped
    }
  }

  /**
   * Check if an effect modifies the oscillator frequency.
   * Effects 0 (none), 4 (fade-in), 5 (fade-out) only affect gain.
   */
  private isPitchChangingEffect(effect: number): boolean {
    return (
      effect === 1 ||
      effect === 2 ||
      effect === 3 ||
      effect === 6 ||
      effect === 7
    )
  }

  /**
   * Check if any notes from `fromIndex` onward have volume > 0.
   */
  private hasRemainingContent(sfx: SFX, fromIndex: number): boolean {
    for (let i = fromIndex; i < NOTES_PER_SFX; i++) {
      if (sfx.notes[i].volume > 0) return true
    }
    return false
  }

  /**
   * Play the next note in the sequence for a channel.
   */
  private playNextNote(channelIndex: number): void {
    if (!this.context || !this.masterGain) return

    const ch = this.channels[channelIndex]
    if (!ch.active || ch.currentSFX < 0) return

    const sfx = this.sfxBank[ch.currentSFX]
    const noteIndex = ch.currentNote

    // Check if we've gone past the end
    if (noteIndex >= NOTES_PER_SFX) {
      this.stopChannel(channelIndex)
      return
    }

    const note = sfx.notes[noteIndex]
    const duration = sfx.speed / 60 // Convert speed ticks to seconds

    // Early stop: if current note is silent and no remaining content (non-looping)
    if (note.volume === 0 && !this.hasRemainingContent(sfx, noteIndex)) {
      if (!ch.looping || ch.stopLooping) {
        this.stopChannel(channelIndex)
        return
      }
    }

    // Check if we can reuse the previous oscillator (same pitch/waveform, no pitch effects)
    const canReuse =
      ch.scheduledNote &&
      note.volume > 0 &&
      note.pitch === ch.scheduledNote.pitch &&
      note.waveform === ch.scheduledNote.waveform &&
      note.waveform !== 6 && // noise always needs a new source
      !this.isPitchChangingEffect(ch.scheduledNote.effect) &&
      !this.isPitchChangingEffect(note.effect)

    if (canReuse) {
      // Reuse oscillator — just update gain, no click
      const now = this.context.currentTime
      const gain = note.volume / 7
      const gainNode = ch.scheduledNote!.gain

      gainNode.gain.cancelScheduledValues(now)

      if (note.effect === 4) {
        // Fade in
        gainNode.gain.setValueAtTime(0, now)
        gainNode.gain.linearRampToValueAtTime(gain, now + duration)
      } else if (note.effect === 5) {
        // Fade out
        gainNode.gain.setValueAtTime(gain, now)
        gainNode.gain.linearRampToValueAtTime(0, now + duration)
      } else {
        gainNode.gain.setValueAtTime(gain, now)
      }

      ch.scheduledNote!.effect = note.effect
    } else {
      // Stop previous note
      if (ch.scheduledNote) {
        this.stopScheduledNote(ch.scheduledNote)
        ch.scheduledNote = null
      }

      // Play the note if it has volume
      if (note.volume > 0) {
        const nextNote =
          noteIndex + 1 < NOTES_PER_SFX ? sfx.notes[noteIndex + 1] : null
        ch.scheduledNote = this.createNote(note, nextNote, duration)
      }
    }

    // Schedule next note
    ch.timerId = setTimeout(() => {
      if (!ch.active) return

      let nextNoteIndex = ch.currentNote + 1

      // Handle looping
      if (ch.looping && !ch.stopLooping) {
        if (nextNoteIndex > sfx.loop_end) {
          nextNoteIndex = sfx.loop_start
        }
      } else if (ch.stopLooping) {
        // Continue playing past loop end until SFX ends
      }

      if (nextNoteIndex >= NOTES_PER_SFX) {
        // SFX complete
        this.stopChannel(channelIndex)
        return
      }

      ch.currentNote = nextNoteIndex
      this.playNextNote(channelIndex)
    }, duration * 1000)
  }

  /**
   * Create and start playing a single note with waveform and effects.
   */
  private createNote(
    note: SFXNote,
    nextNote: SFXNote | null,
    duration: number,
  ): ScheduledNote {
    const ctx = this.context!
    const now = ctx.currentTime
    const frequency = pitchToFrequency(note.pitch)
    const gain = note.volume / 7

    // Create gain node for this note
    const gainNode = ctx.createGain()
    gainNode.connect(this.masterGain!)

    let sourceNode: OscillatorNode | AudioBufferSourceNode

    if (note.waveform === 6) {
      // Pitched noise: bandpass-filtered noise
      sourceNode = this.createNoiseSource(ctx, frequency, gainNode)
    } else {
      // Oscillator-based waveforms
      const osc = ctx.createOscillator()
      this.setWaveform(osc, note.waveform)
      osc.frequency.setValueAtTime(frequency, now)
      osc.connect(gainNode)
      sourceNode = osc

      // Apply pitch-based effects
      this.applyEffect(
        note.effect,
        osc,
        gainNode,
        frequency,
        gain,
        duration,
        now,
        nextNote,
      )
    }

    // Set initial gain (may be overridden by effects)
    if (note.effect !== 4) {
      // Not fade-in, set gain immediately
      gainNode.gain.setValueAtTime(gain, now)
    }

    // Apply volume effects
    if (note.effect === 4) {
      // Fade in
      gainNode.gain.setValueAtTime(0, now)
      gainNode.gain.linearRampToValueAtTime(gain, now + duration)
    } else if (note.effect === 5) {
      // Fade out
      gainNode.gain.setValueAtTime(gain, now)
      gainNode.gain.linearRampToValueAtTime(0, now + duration)
    }

    sourceNode.start(now)
    // Don't auto-stop — lifecycle is managed by playNextNote/stopChannel
    // so the oscillator can be reused for same-pitch note continuity.

    return {
      oscillator: sourceNode,
      gain: gainNode,
      pitch: note.pitch,
      waveform: note.waveform,
      effect: note.effect,
    }
  }

  /**
   * Create a bandpass-filtered noise source for pitched noise waveform.
   */
  private createNoiseSource(
    ctx: AudioContext,
    frequency: number,
    output: GainNode,
  ): AudioBufferSourceNode {
    const source = ctx.createBufferSource()
    source.buffer = this.noiseBuffer!
    source.loop = true

    // Bandpass filter centered on the pitch frequency.
    // Lower Q for wider bandwidth — sounds more like noise/explosion.
    const filter = ctx.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = frequency
    filter.Q.value = 2

    source.connect(filter)
    filter.connect(output)

    return source
  }

  /**
   * Set the waveform type on an oscillator.
   */
  private setWaveform(osc: OscillatorNode, waveform: number): void {
    switch (waveform) {
      case 0:
        osc.type = 'sine'
        break
      case 1:
        osc.type = 'triangle'
        break
      case 2:
        osc.type = 'sawtooth'
        break
      case 3: // Square (50% pulse via PeriodicWave to match other pulse volumes)
        if (this.squareWave) osc.setPeriodicWave(this.squareWave)
        break
      case 4: // 25% pulse
        if (this.pulseWave25) osc.setPeriodicWave(this.pulseWave25)
        break
      case 5: // 12.5% pulse
        if (this.pulseWave12) osc.setPeriodicWave(this.pulseWave12)
        break
      // case 6 is handled separately (noise)
      case 7: // Rounded sawtooth
        if (this.roundedSaw) osc.setPeriodicWave(this.roundedSaw)
        break
      default:
        osc.type = 'sine'
        break
    }
  }

  /**
   * Apply a note effect to an oscillator.
   */
  private applyEffect(
    effect: number,
    osc: OscillatorNode,
    _gainNode: GainNode,
    frequency: number,
    _gain: number,
    duration: number,
    now: number,
    nextNote: SFXNote | null,
  ): void {
    switch (effect) {
      case 0: // None
        break

      case 1: // Slide - pitch glides to next note over duration
        if (nextNote) {
          const nextFreq = pitchToFrequency(nextNote.pitch)
          osc.frequency.setValueAtTime(frequency, now)
          osc.frequency.linearRampToValueAtTime(nextFreq, now + duration)
        }
        break

      case 2: {
        // Vibrato - oscillate pitch ±2 semitones at 4 Hz
        const vibratoRate = 4 // Hz
        const vibratoDepth = 2 // semitones
        const steps = Math.max(8, Math.ceil(duration * vibratoRate * 16))
        const stepDuration = duration / steps

        const freqHigh = frequency * Math.pow(2, vibratoDepth / 12)
        const freqLow = frequency * Math.pow(2, -vibratoDepth / 12)

        // Build a vibrato curve using setValueAtTime steps
        for (let i = 0; i < steps; i++) {
          const t = now + i * stepDuration
          const phase = Math.sin(2 * Math.PI * vibratoRate * (i * stepDuration))
          const freq =
            frequency +
            (phase > 0 ? freqHigh - frequency : freqLow - frequency) *
              Math.abs(phase)
          osc.frequency.setValueAtTime(freq, t)
        }
        break
      }

      case 3: // Drop - pitch falls by 2 octaves over duration
        osc.frequency.setValueAtTime(frequency, now)
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(frequency / 4, 1), // 2 octaves down, min 1 Hz
          now + duration,
        )
        break

      case 4: // Fade in - handled in createNote via gainNode
        break

      case 5: // Fade out - handled in createNote via gainNode
        break

      case 6: {
        // Arpeggio fast - cycle (root, +4, +7) at 60 Hz
        const arpFreqs = [
          frequency,
          frequency * Math.pow(2, 4 / 12), // +4 semitones (major third)
          frequency * Math.pow(2, 7 / 12), // +7 semitones (perfect fifth)
        ]
        const arpRate = 1 / 60 // 60 Hz
        const totalSteps = Math.floor(duration / arpRate)
        for (let i = 0; i < totalSteps; i++) {
          osc.frequency.setValueAtTime(arpFreqs[i % 3], now + i * arpRate)
        }
        break
      }

      case 7: {
        // Arpeggio slow - cycle (root, +4, +7) at 30 Hz
        const arpFreqs = [
          frequency,
          frequency * Math.pow(2, 4 / 12),
          frequency * Math.pow(2, 7 / 12),
        ]
        const arpRate = 1 / 30 // 30 Hz
        const totalSteps = Math.floor(duration / arpRate)
        for (let i = 0; i < totalSteps; i++) {
          osc.frequency.setValueAtTime(arpFreqs[i % 3], now + i * arpRate)
        }
        break
      }
    }
  }
}
